import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Spinner, Badge } from 'flowbite-react';
import { useGetTicketById } from 'src/hooks/support.hooks';
import { useMutation } from 'react-query';
import { addTicketMessage, rateTicket, reopenTicket, escalateTicket } from 'src/apis/support';
import { useToast } from 'src/context/ToastProvider';
import { SupportTicketStatus, MessageSenderType } from 'types';
import { get } from 'lodash';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getSupportQuillModules, attachSupportQuillPasteHandler } from './supportQuillConfig';

const QUILL_MODULES = getSupportQuillModules();

const stripHtml = (html: string): string => {
	if (!html) return '';
	const tmp = document.createElement('div');
	tmp.innerHTML = html;
	return (tmp.textContent || tmp.innerText || '').trim();
};

const slugToTitle = (slug: string) =>
	slug
		.split('-')
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
		.join(' ');

interface SupportTicketChatProps {
	ticketId: string;
	onClose: () => void;
}

const SupportTicketChat: React.FC<SupportTicketChatProps> = ({ ticketId, onClose }) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const [message, setMessage] = useState('');
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const quillRef = useRef<any>(null);
	const pasteCleanupRef = useRef<(() => void) | null>(null);
	const [satisfactionRating, setSatisfactionRating] = useState<number | null>(null);
	const [showRating, setShowRating] = useState(false);

	const { data: ticketData, isLoading } = useGetTicketById(ticketId);
	const ticket = (ticketData as any)?.data;

	const urlCategory = searchParams.get('category');
	const urlSubcategory = searchParams.get('subcategory');
	// Prefer populated category/subcategory from ticket (labels); fallback to URL slug when it looks like a slug (not an ObjectId)
	const looksLikeId = (s: string) => /^[a-f0-9]{24}$/i.test(s || '');
	const categoryLabel =
		(ticket?.category?.label || ticket?.category?.name) ||
		(urlCategory && !looksLikeId(urlCategory) ? slugToTitle(urlCategory) : '') ||
		(ticket?.subject || 'Support');
	const subcategoryLabel =
		(ticket?.subcategory?.label || ticket?.subcategory?.name) ||
		(urlSubcategory && !looksLikeId(urlSubcategory) ? slugToTitle(urlSubcategory) : '') ||
		'';
	const createdDate = ticket?.createdAt
		? new Date(ticket.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
		: '';

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		scrollToBottom();
	}, [ticket?.messages]);

	// Attach paste handler so pasted images upload to S3 instead of Base64
	useEffect(() => {
		const timer = setTimeout(() => {
			const editor = quillRef.current?.getEditor?.();
			if (editor) {
				pasteCleanupRef.current?.();
				pasteCleanupRef.current = attachSupportQuillPasteHandler(editor);
			}
		}, 100);
		return () => {
			clearTimeout(timer);
			pasteCleanupRef.current?.();
			pasteCleanupRef.current = null;
		};
	}, []);

	const addMessageMutation = useMutation(['addTicketMessage'], (data: { message: string }) =>
		addTicketMessage(ticketId, data),
	);

	const rateTicketMutation = useMutation(['rateTicket'], (data: any) => rateTicket(ticketId, data));
	const reopenTicketMutation = useMutation(['reopenTicket'], (data?: any) => reopenTicket(ticketId, data));
	const escalateTicketMutation = useMutation(['escalateTicket'], (data?: any) => escalateTicket(ticketId, data));

	const handleSendMessage = () => {
		if (!message.trim()) return;

		// Send full HTML (including embedded images) so it is stored and shown to AM/Admin
		addMessageMutation.mutate(
			{ message },
			{
				onSuccess() {
					setMessage('');
					queryClient.refetchQueries(['getTicketById', ticketId]);
					queryClient.refetchQueries(['getMyTickets']);
				},
				onError(error: any) {
					toast({
						title: get(error, ['response', 'data', 'message'], 'Failed to send message'),
						type: 'error',
					});
				},
			},
		);
	};

	const handleRateTicket = (isSatisfied: boolean) => {
		if (!satisfactionRating) {
			toast({
				title: 'Please select a rating',
				type: 'error',
			});
			return;
		}

		rateTicketMutation.mutate(
			{
				satisfactionRating,
				isSatisfied,
			},
			{
				onSuccess() {
					toast({
						title: 'Thank you for your feedback!',
						type: 'success',
					});
					setShowRating(false);
					queryClient.refetchQueries(['getTicketById', ticketId]);
				},
				onError(error: any) {
					toast({
						title: get(error, ['response', 'data', 'message'], 'Failed to submit rating'),
						type: 'error',
					});
				},
			},
		);
	};

	const handleReopenTicket = () => {
		reopenTicketMutation.mutate(
			{},
			{
				onSuccess() {
					toast({
						title: 'Ticket reopened successfully',
						type: 'success',
					});
					queryClient.refetchQueries(['getTicketById', ticketId]);
					queryClient.refetchQueries(['getMyTickets']);
				},
				onError(error: any) {
					toast({
						title: get(error, ['response', 'data', 'message'], 'Failed to reopen ticket'),
						type: 'error',
					});
				},
			},
		);
	};

	const handleEscalateTicket = () => {
		const reason = window.prompt('Please provide a reason for escalation (optional):');
		escalateTicketMutation.mutate(
			{ reason: reason || undefined },
			{
				onSuccess() {
					toast({
						title: 'Ticket escalated successfully',
						type: 'success',
					});
					queryClient.refetchQueries(['getTicketById', ticketId]);
					queryClient.refetchQueries(['getMyTickets']);
				},
				onError(error: any) {
					toast({
						title: get(error, ['response', 'data', 'message'], 'Failed to escalate ticket'),
						type: 'error',
					});
				},
			},
		);
	};

	if (isLoading) {
		return (
			<Card className="bg-white dark:bg-[#1F2A37]">
				<div className="flex justify-center py-8">
					<Spinner size="xl" />
				</div>
			</Card>
		);
	}

	if (!ticket) {
		return (
			<Card className="bg-white dark:bg-[#1F2A37]">
				<div className="text-center py-8 text-gray-500">Ticket not found</div>
			</Card>
		);
	}

	const isResolved = ticket.status === SupportTicketStatus.RESOLVED;
	const isClosed = ticket.status === SupportTicketStatus.RESOLVED || ticket.status === 'closed';

	const formatMessageTime = (date: string) =>
		new Date(date).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });

	const statusDisplay = ticket.status ? (ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1).toLowerCase()) : ticket.status;
	const messagesList = ticket.messages || [];

	// Build "Chat with [Manager name]" placement from escalationConfig by assignedAt:
	// For each level, show "Chat with [name]" before the first message with createdAt >= that level's assignedAt.
	const escalationConfig = ticket.escalationConfig || [];
	const chatWithBeforeMessage: Record<number, string[]> = {};
	for (const ec of escalationConfig) {
		const assignedAt = ec.assignedAt ? new Date(ec.assignedAt).getTime() : null;
		const name = ec.assignedTo && typeof ec.assignedTo === 'object' && ec.assignedTo.name ? ec.assignedTo.name : null;
		if (!name) continue;
		// First message (by index) whose createdAt is on or after this level's assignedAt
		let insertAtIndex = messagesList.length;
		if (assignedAt != null) {
			const idx = messagesList.findIndex((m: any) => m.createdAt && new Date(m.createdAt).getTime() >= assignedAt);
			if (idx >= 0) insertAtIndex = idx;
		} else {
			// No assignedAt: show before first message (index 0)
			insertAtIndex = 0;
		}
		if (!chatWithBeforeMessage[insertAtIndex]) chatWithBeforeMessage[insertAtIndex] = [];
		chatWithBeforeMessage[insertAtIndex].push(name);
	}

	const firstAgentIndex = messagesList.findIndex((m: any) => m.senderType === MessageSenderType.AGENT);
	// Fallback: no escalationConfig (e.g. old ticket) — show single "Chat with [current assignee or first agent]" before first agent message
	if (escalationConfig.length === 0 && messagesList.length > 0) {
		const fallbackName =
			ticket.currentAssignedTo?.name ||
			(firstAgentIndex >= 0 ? messagesList[firstAgentIndex]?.sender?.name : null) ||
			'Support';
		if (fallbackName) {
			const idx = firstAgentIndex >= 0 ? firstAgentIndex : 0;
			if (!chatWithBeforeMessage[idx]) chatWithBeforeMessage[idx] = [];
			chatWithBeforeMessage[idx].push(fallbackName);
		}
	}

	// Show Escalate only when there is a higher level (managers in DB); L4 = max, so no escalate at L4. Reopen always visible when resolved.
	const canEscalate = ticket.currentEscalationLevel !== 'L4';

	return (
		<Card className="bg-white dark:bg-[#1F2A37] flex flex-col min-h-0 h-full border-0 shadow-none">

			{/* Body: scrollable - intro, TODAY, then messages (reference: scroll stays inside, header/input fixed) */}
			<div className="flex-1 min-h-0 flex flex-col overflow-hidden mb-2">
				<div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-gray-50 dark:bg-gray-800 p-4">
					{/* 1. Intro text - no border */}
					<div className="mb-4 p-3 rounded-lg bg-gray-200/70 dark:bg-gray-700/70 text-gray-700 dark:text-gray-300 text-sm border-0 shadow-none">
						Share the details of your issue so our support team can reach out with the right help.
					</div>
					{/* 2. TODAY separator */}
					<div className="flex items-center gap-2 my-4">
						<div className="flex-1 border-t border-gray-300 dark:border-gray-600" />
						<span className="text-xs font-medium text-gray-500 dark:text-gray-400">TODAY</span>
						<div className="flex-1 border-t border-gray-300 dark:border-gray-600" />
					</div>
					{/* 3. First message = student ticket message (subject) - right, purple */}
					{ticket.subject && (
						<div className="mb-4 flex justify-end">
							<div className="max-w-[75%] rounded-lg p-3 bg-[#7777E7] text-white">
								<div className="support-ticket-content prose prose-sm prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: ticket.subject }} />
								<div className="text-xs mt-1 opacity-90 text-right">
									{formatMessageTime(ticket.createdAt)}
								</div>
							</div>
						</div>
					)}
					{/* 4. Messages: show "Chat with [Name]" before the first message on or after each level's assignedAt (from escalationConfig) */}
					{messagesList.map((msg: any, index: number) => {
						const isUser = msg.senderType === MessageSenderType.USER;
						const isAgent = msg.senderType === MessageSenderType.AGENT;
						const isSystem = msg.senderType === MessageSenderType.SYSTEM;
						const isFirstAgent = isAgent && index === firstAgentIndex;
						const chatWithNamesAtThisIndex = chatWithBeforeMessage[index] || [];
						const agentName = msg.sender?.name || 'Support';

						return (
							<div key={msg._id}>
								{/* Show "Chat with [Name]" for each escalation level that belongs before this message */}
								{chatWithNamesAtThisIndex.map((name, idx) => (
									<div key={`chatwith-${index}-${idx}-${name}`} className="flex items-center gap-2 my-3">
										<div className="flex-1 border-t border-gray-300 dark:border-gray-600" />
										<span className="text-xs font-medium text-purple-600 dark:text-purple-400">Chat with {name}</span>
										<div className="flex-1 border-t border-gray-300 dark:border-gray-600" />
									</div>
								))}
								<div className={`mb-4 flex ${isUser ? 'justify-end' : 'justify-start'}`}>
									{!isUser && msg.sender && (
										<div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm font-semibold text-blue-700 dark:text-blue-200 mr-2">
											{(msg.sender.name || 'A').charAt(0).toUpperCase()}
										</div>
									)}
									<div
										className={`max-w-[75%] rounded-lg p-3 ${isUser
											? 'bg-[#7777E7] text-white'
											: (isFirstAgent || (isAgent && chatWithNamesAtThisIndex.length > 0))
												? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 border border-blue-200 dark:border-blue-800'
												: isSystem
													? 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
													: 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600'
											}`}
									>
										{!isUser && isAgent && msg.sender && (
											<div className="text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">{msg.sender.name}</div>
										)}
										<div className="support-ticket-content prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap">{msg.message || ''}</div>
										{msg.attachments?.length > 0 && (
											<div className="mt-2 flex flex-wrap gap-2">
												{msg.attachments.map((url: string) => (
													<a key={url} href={url} target="_blank" rel="noopener noreferrer" className="block">
														<img src={url} alt="Attachment" className="max-h-48 rounded object-contain border border-gray-200 dark:border-gray-600" />
													</a>
												))}
											</div>
										)}
										<div className={`text-xs mt-1 opacity-80 ${isUser ? 'text-right' : ''}`}>
											{formatMessageTime(msg.createdAt)}
										</div>
									</div>
								</div>
							</div>
						);
					})}
					{/* "Chat with [Name]" for levels whose assignedAt is after all messages */}
					{(chatWithBeforeMessage[messagesList.length] || []).map((name, idx) => (
						<div key={`chatwith-end-${idx}-${name}`} className="flex items-center gap-2 my-3">
							<div className="flex-1 border-t border-gray-300 dark:border-gray-600" />
							<span className="text-xs font-medium text-purple-600 dark:text-purple-400">Chat with {name}</span>
							<div className="flex-1 border-t border-gray-300 dark:border-gray-600" />
						</div>
					))}
					<div ref={messagesEndRef} />
				</div>
			</div>

			{/* Resolved: always show status + question; Escalate/Reopen only when user said No */}
			{isResolved && (
				<div className="mb-4 p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800">
					<p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3">This ticket has been marked as resolved</p>
					<p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Did we solve your issue?</p>
					{(!ticket.isSatisfied && !showRating) && (
						<div className="flex items-center gap-2 mb-4">
							<button
								type="button"
								onClick={() => { setSatisfactionRating(5); handleRateTicket(true); }}
								className="p-2 rounded-full border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
								aria-label="Yes"
							>
								👍
							</button>
							<button
								type="button"
								onClick={() => setShowRating(true)}
								className="p-2 rounded-full border border-purple-300 dark:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30"
								aria-label="No"
							>
								👎
							</button>
						</div>
					)}
					{/* Escalate and Reopen only when user selected No */}
					{(showRating || ticket.isSatisfied === false) && (
						<div className="flex flex-wrap gap-2">
							{canEscalate && (
								<Button
									size="sm"
									color="gray"
									outline
									onClick={handleEscalateTicket}
									disabled={escalateTicketMutation.isLoading}
								>
									{escalateTicketMutation.isLoading ? <Spinner size="sm" /> : 'Escalate to support specialist'}
								</Button>
							)}
							<Button
								size="sm"
								color="purplePlaced"
								onClick={handleReopenTicket}
								disabled={reopenTicketMutation.isLoading}
							>
								{reopenTicketMutation.isLoading ? <Spinner size="sm" /> : 'Reopen'}
							</Button>
						</div>
					)}
				</div>
			)}

			{/* Message Input - ReactQuill like reference image 4 */}
			{!isClosed && (
				<div className="flex-shrink-0 mt-4">
					<div className="support-fallback-quill min-w-0 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-700">
						<ReactQuill
							ref={quillRef}
							theme="snow"
							value={message}
							onChange={setMessage}
							placeholder="Type message here"
							modules={QUILL_MODULES}
							className="[&_.ql-container]:h-[100px] [&_.ql-container]:min-h-[100px] [&_.ql-container]:overflow-y-auto [&_.ql-editor]:min-h-[98px]"
						/>
					</div>
					<div className="flex justify-end mt-2">
						<Button
							color="purplePlaced"
							onClick={handleSendMessage}
							disabled={!message.trim() || addMessageMutation.isLoading}
						>
							{addMessageMutation.isLoading ? <Spinner size="sm" className="mr-2" /> : null}
							Send
						</Button>
					</div>
				</div>
			)}

			{isClosed && !isResolved && (
				<div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
					<p className="text-sm text-gray-600 dark:text-gray-400 mb-2">This ticket has been resolved.</p>
					<Button size="sm" color="purplePlaced" onClick={handleReopenTicket} disabled={reopenTicketMutation.isLoading}>
						{reopenTicketMutation.isLoading ? <Spinner size="sm" /> : 'Reopen'}
					</Button>
				</div>
			)}
		</Card>
	);
};

export default SupportTicketChat;

